import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { COOKIE_SELECTED_TEAM } from '@constants/cookies';
import { COMPANY_INFO_ROUTE, SELECT_VENUE_ROUTE, USER_SETTINGS_ROUTE } from '@constants/routes';
import { AuthContext } from '@contexts/auth/auth-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { ADMIN_COMPANY_PROFILER_ROLES, ROLE_PROMOTER, ROLE_TEAM_LEADER } from '@domain/company';
import { useGetAuthContext } from '@hooks/auth/use-get-auth-context';
import { useSetProfiler } from '@hooks/profiler/use-set-profiler';
import { usePostHog } from '@posthog/react';
import { identifyCompanyContext } from '@posthog-app/company-identification';
import Cookies from 'js-cookie';

// import { isProductionMode } from '@utils/get-environment/get-environment';
import { CompanyAuthContextValue } from './company-auth-context.type';

export const CompanyAuthContext = createContext<CompanyAuthContextValue>({
  isFinished: false,
  permissions: [],
  channels: [],
  role: undefined,
  teams: [],
  setCompanyAuthId: () => {},
  setCompanyClubId: () => {},
});

export const CompanyAuthContextProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const companyId = pathname.split('/')[1];
  const [companyAuthId, setCompanyAuthId] = useState('');
  const [companyClubId, setCompanyClubId] = useState('');
  const { isCompanyAuthEnabled } = useContext(FeatureFlagContext);
  const { isImpersonated } = useContext(AuthContext);
  const posthog = usePostHog();
  const isClubIdOptional = useMatch(COMPANY_INFO_ROUTE);
  const {
    data: companyAuthResponse,
    refetch,
    isFetching,
    isSuccess,
    isError,
  } = useGetAuthContext({
    companyId: companyAuthId,
    clubId: isClubIdOptional ? undefined : companyClubId,
    options: { enabled: !!companyAuthId && isCompanyAuthEnabled && (!!isClubIdOptional || !!companyClubId) },
  });
  const { mutate: setProfiler } = useSetProfiler();

  const unprotectedRoutes = [SELECT_VENUE_ROUTE, USER_SETTINGS_ROUTE];

  useEffect(() => {
    if (companyAuthResponse && !isFetching && !unprotectedRoutes.some((route) => pathname.includes(route))) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (companyAuthResponse) {
      if (companyAuthResponse.user.isSuperAdmin) {
        setProfiler({});
      } else {
        if (companyAuthResponse.role.name === ROLE_PROMOTER) {
          setProfiler({ groupType: 'promoter', groupId: companyAuthResponse.channels[0].id });
        }
        if (companyAuthResponse.role.name === ROLE_TEAM_LEADER) {
          const selectedTeamId = Cookies.get(COOKIE_SELECTED_TEAM);
          setProfiler({
            groupType: 'promoterManager',
            groupId: selectedTeamId ? parseInt(selectedTeamId) : companyAuthResponse.teams[0].id,
          });
        }
        if (
          ADMIN_COMPANY_PROFILER_ROLES.includes(companyAuthResponse.role.name) &&
          !unprotectedRoutes.some((route) => pathname.includes(route)) &&
          !!companyId
        ) {
          setProfiler({ groupType: 'company', groupId: Number(companyId) });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyAuthResponse, setProfiler, companyId]);

  // Super-admin and impersonated sessions stay out of company analytics so internal
  // activity doesn't pollute customer segmentation (parity with DAU tracking).
  // The auth/context response only carries the company UUID, so the legacy int id —
  // the one used by feature-flag payloads and the profiler — comes from the URL,
  // same as the profiler effect above. NaN on non-venue routes skips the call.
  useEffect(() => {
    const legacyCompanyId = Number.parseInt(companyId);
    if (Number.isNaN(legacyCompanyId)) return;
    if (!companyAuthResponse || !posthog || isImpersonated || companyAuthResponse.user.isSuperAdmin) return;
    identifyCompanyContext(posthog, {
      userId: companyAuthResponse.user.id,
      companyId: legacyCompanyId,
      role: companyAuthResponse.role.name,
    });
  }, [companyAuthResponse, posthog, isImpersonated, companyId]);

  const memoizedContextValue = useMemo<CompanyAuthContextValue>(
    () => ({
      isFinished: isSuccess || isError,
      permissions: companyAuthId && companyClubId ? companyAuthResponse?.permissions : [],
      role: companyAuthId && companyClubId ? companyAuthResponse?.role : undefined,
      channels: companyAuthId && companyClubId ? companyAuthResponse?.channels : [],
      teams: companyAuthId && companyClubId ? companyAuthResponse?.teams : [],
      setCompanyAuthId,
      setCompanyClubId,
    }),
    [companyAuthResponse, companyAuthId, companyClubId, isSuccess, isError]
  );

  return <CompanyAuthContext.Provider value={{ ...memoizedContextValue }}>{children}</CompanyAuthContext.Provider>;
};
